import type { MaDealBand } from '../../ma-deal-gross-synergy-by-band-formula';
import type { SynergyFigure } from '../../synergy-risk';
import type { SynergyLoopStageResult } from '../contracts/synergy-loop.v1';

export const ASK_VIEW_LOOP2_GAP_CODES = [
  'missing_publication', 'no_current_snapshot', 'no_options', 'no_recovery_actions',
  'missing_figure', 'missing_timing', 'missing_label', 'duplicate_identity',
  'invalid_unit', 'unreadable_publication', 'needs_reevaluation', 'evidence_unverifiable',
] as const;
export type AskViewLoop2GapCode = typeof ASK_VIEW_LOOP2_GAP_CODES[number];
export type AskViewLoop2Gap = Readonly<{ code: AskViewLoop2GapCode; message: string }>;
export const RECOVERY_NEEDS_REEVALUATION = {
  code: 'needs_reevaluation',
  message: 'Current impact needs re-evaluation: its source data changed since this action was approved.',
} as const satisfies AskViewLoop2Gap;
export type AskViewLoop2Source = Readonly<{ datasetRef: string; revisionId: string }>;
export const RECOVERY_EVIDENCE_UNVERIFIABLE = {
  code: 'evidence_unverifiable',
  message: "This recovery action's saved evidence can't be verified against current records, so its impact isn't shown.",
} as const satisfies AskViewLoop2Gap;

/** Every cell is an existing server-authored figure; alternatives are never summed. */
export type AskViewOptionEffectBands = Readonly<{
  low: SynergyFigure | null; base: SynergyFigure | null; high: SynergyFigure | null;
}>;
export type AskViewPublishedOption = Readonly<{
  id: string; label: string | null;
  commitmentRef: string; commitmentLabel: string | null;
  lever: string | null; leverLabel: string | null;
  runRateEffect: AskViewOptionEffectBands;
  oneTimeCost: AskViewOptionEffectBands;
  monthsToEffect: SynergyFigure | null;
  gaps: readonly AskViewLoop2Gap[];
}>;
export type AskViewPublishedOptions = Readonly<{
  evaluations?: readonly Readonly<{ commitmentRef: string; label: string; result: SynergyLoopStageResult }>[];
  committed?: SynergyFigure | null;
  /** Server-authored inventory copy; never establishes a recommended next step. */
  summary?: string;
  state: 'available' | 'gap'; asOf: string | null; source: AskViewLoop2Source | null;
  options: readonly AskViewPublishedOption[]; gaps: readonly AskViewLoop2Gap[];
}>;

export type AskViewRecoveryScenarioBand = Readonly<{
  forecast: SynergyFigure | null; target: SynergyFigure | null; gap: SynergyFigure | null;
  incrementalRecovery: SynergyFigure | null; scenario: SynergyFigure | null;
  residual: SynergyFigure | null; implementationCost: SynergyFigure | null;
}>;
export type AskViewPublishedRecoveryScenario = Readonly<{
  id: string; label: string | null; commitmentLabel: string | null; leverLabel: string | null;
  bands: Readonly<Record<MaDealBand, AskViewRecoveryScenarioBand>>;
  gaps: readonly AskViewLoop2Gap[];
}>;
export type AskViewRecoveryScenario = Readonly<{
  state: 'available' | 'gap'; asOf: string | null; source: AskViewLoop2Source | null;
  message?: string;
  scenarios: readonly AskViewPublishedRecoveryScenario[]; gaps: readonly AskViewLoop2Gap[];
}>;
